import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Loader2,
  Undo2,
  Warehouse,
} from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { NumericInput } from "../ui/numeric-input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";

/**
 * Dialog de correction d'un BL livré par erreur — Phase 63.
 *
 * Workflow : 1) admin coche les lignes du BL livrées par erreur · 2) renseigne
 * la quantité à retourner par ligne · 3) le système crée des TRANSFER inverses
 * vers Conthey (ou un autre dépôt de retour). Le BL original reste intact (les
 * lignes ne sont PAS supprimées) — la traçabilité HACCP est conservée et
 * l'événement est tracé dans `correction_movement_ids` + `corrected_at`.
 *
 * Props :
 *   - bl              : le BL complet (avec lines[], from_distributor_id, etc.)
 *   - distributors    : liste des distributeurs (pour Select destination)
 *   - onDone          : callback de refresh après succès
 *   - trigger         : élément React déclencheur (typiquement Button)
 */
export default function DeliveryCorrectionDialog({ bl, distributors = [], onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({}); // { lineId: quantityToReturn }
  const [destinationId, setDestinationId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleOpenChange = (next) => {
    if (next) {
      setSelected({});
      setDestinationId(bl?.from_distributor_id || "");
      setNotes("");
    }
    setOpen(next);
  };

  const toggleLine = (line) => {
    setSelected((prev) => {
      const copy = { ...prev };
      if (copy[line.id] != null) {
        delete copy[line.id];
      } else {
        copy[line.id] = String(line.quantity);
      }
      return copy;
    });
  };

  const setQty = (lineId, val) => {
    setSelected((prev) => ({ ...prev, [lineId]: val }));
  };

  const selectedLines = useMemo(() => {
    if (!bl?.lines) return [];
    return bl.lines.filter((l) => selected[l.id] != null);
  }, [bl, selected]);

  const totalToReturn = useMemo(() => {
    return selectedLines.reduce((acc, l) => acc + (Number(selected[l.id]) || 0), 0);
  }, [selectedLines, selected]);

  const allValid = selectedLines.length > 0 && selectedLines.every((l) => {
    const q = Number(selected[l.id]);
    return Number.isFinite(q) && q > 0 && q <= (l.quantity || 0);
  });

  const returnDistName = distributors.find((d) => d.id === destinationId)?.name || "(non choisi)";

  const submit = async () => {
    if (!allValid) return;
    setSubmitting(true);
    try {
      const payload = {
        incorrect_lines: selectedLines.map((l) => ({
          line_id: l.id,
          quantity_to_return: Number(selected[l.id]),
        })),
        return_to_distributor_id: destinationId || null,
        notes: notes.trim() || "",
      };
      await api.post(`/delivery-notes/${bl.id}/correct`, payload);
      toast.success(`${selectedLines.length} ligne(s) retournée(s) vers ${returnDistName}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la correction");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div onClick={(e) => { e.stopPropagation(); handleOpenChange(true); }}>
        {trigger}
      </div>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bl-correct-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-[#8C6B2F]" />
            Corriger la livraison erronée
          </DialogTitle>
          <DialogDescription>
            BL <strong className="font-mono">{bl?.reference}</strong> ·{" "}
            destinataire <strong>{bl?.distributor_name}</strong>.<br />
            Cochez les lignes livrées par erreur. Le stock sera renvoyé vers Conthey
            (ou un autre dépôt) via des mouvements TRANSFER. Le BL original n&apos;est PAS
            modifié — la traçabilité HACCP est conservée et la correction est tracée.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Lignes du BL avec checkbox + quantité */}
          <div>
            <Label className="mb-2 block">Lignes erronées à retourner</Label>
            <div className="border border-[#E5E5E0] rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F0]">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#575A57]">Produit</th>
                    <th className="text-left px-2 py-2 text-xs font-semibold text-[#575A57]">Lot · DLC</th>
                    <th className="text-right px-2 py-2 text-xs font-semibold text-[#575A57]">Livré</th>
                    <th className="text-right px-2 py-2 text-xs font-semibold text-[#575A57]">Retour</th>
                  </tr>
                </thead>
                <tbody>
                  {(bl?.lines || []).map((l) => {
                    const checked = selected[l.id] != null;
                    return (
                      <tr
                        key={l.id}
                        className={`border-t border-[#E5E5E0] ${checked ? "bg-[#FBF4E9]" : "hover:bg-[#FAFAF7]"}`}
                        data-testid={`bl-correct-line-${l.id}`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLine(l)}
                            data-testid={`bl-correct-check-${l.id}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-[#1A1C1A]">{l.product_name}</div>
                          <div className="text-[10px] text-[#A3A6A3] font-mono">{l.product_sku}</div>
                        </td>
                        <td className="px-2 py-2 text-xs">
                          <span className="font-mono text-[#575A57]">{l.lot_number || "—"}</span>
                          {l.dlc && <span className="text-[#A3A6A3] ml-1.5">· DLC {l.dlc}</span>}
                        </td>
                        <td className="text-right px-2 py-2 font-mono text-xs">{l.quantity} {l.unit}</td>
                        <td className="text-right px-2 py-2">
                          {checked ? (
                            <NumericInput
                              value={selected[l.id]}
                              onChange={(e) => setQty(l.id, e.target.value)}
                              className="h-7 w-20 text-xs text-right inline-block"
                              data-testid={`bl-correct-qty-${l.id}`}
                            />
                          ) : (
                            <span className="text-[#A3A6A3] text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Destination du retour */}
          <div>
            <Label className="mb-1.5 block">Retourner vers</Label>
            <Select value={destinationId} onValueChange={setDestinationId}>
              <SelectTrigger data-testid="bl-correct-destination">
                <SelectValue placeholder="Choisir le dépôt de retour…" />
              </SelectTrigger>
              <SelectContent>
                {distributors
                  .filter((d) => d.id !== bl?.distributor_id)
                  .map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] uppercase text-[#A3A6A3] font-mono">{d.type}</span>
                        {d.name}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#A3A6A3] mt-1">
              Par défaut : dépôt source du BL ({distributors.find((d) => d.id === bl?.from_distributor_id)?.name || "—"}).
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label className="mb-1.5 block">Raison de la correction</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex : Erreur de préparation — Pad Thaï envoyé au lieu de Curry Rouge."
              rows={2}
              data-testid="bl-correct-notes"
            />
          </div>

          {/* Récap */}
          {selectedLines.length > 0 && (
            <div className="p-3 rounded bg-[#FBF4E9] border border-[#E8D3A8] flex items-start gap-2" data-testid="bl-correct-summary">
              <AlertTriangle className="w-4 h-4 text-[#8C6B2F] shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed text-[#8C6B2F]">
                <strong>{selectedLines.length} ligne(s)</strong> · <strong className="font-mono">{totalToReturn} barquette(s)</strong> retournée(s)
                vers <strong>{returnDistName}</strong>.{" "}
                Cette action crée {selectedLines.length} mouvement(s) TRANSFER. Aucune ligne du BL ne sera supprimée.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!allValid || !destinationId || submitting}
            className="bg-[#8C6B2F] hover:bg-[#6F5424]"
            data-testid="bl-correct-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Warehouse className="w-4 h-4 mr-2" />}
            Confirmer le retour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
